import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { toast } from "react-toastify";
import { lostSalesEventsService } from "../../utils/services/lostSalesEventsService";
import SettingsSection, {
  SectionAction,
  settingsButtonSx,
  settingsText,
} from "../settings-section";

const iconButtonSx = { color: "var(--text-secondary)" };

const emptyEvent = { name: "", start_date: "", end_date: "", active: true };

const LostSalesEventsSettings = () => {
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await lostSalesEventsService.list();
      setEvents(response.data || response || []);
    } catch (requestError) {
      console.error("Unable to load Lost Sales events:", requestError);
      toast.error("Unable to load Lost Sales events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const openAdd = () => {
    setEditingEvent(null);
    setForm(emptyEvent);
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      name: event.name,
      start_date: event.start_date,
      end_date: event.end_date,
      active: event.active !== false,
    });
    setError("");
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      setError("Name, start date, and end date are required.");
      return;
    }
    if (form.start_date > form.end_date) {
      setError("End date must be on or after start date.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        active: form.active,
      };
      if (editingEvent) {
        await lostSalesEventsService.patch(editingEvent._id, payload);
      } else {
        await lostSalesEventsService.create(payload);
      }
      setDialogOpen(false);
      toast.success("Lost Sales event saved.");
      await loadEvents();
    } catch (requestError) {
      console.error("Unable to save Lost Sales event:", requestError);
      setError(
        requestError?.response?.data?.message ||
          "Unable to save the Lost Sales event.",
      );
    } finally {
      setLoading(false);
    }
  };

  const setActive = async (event, active) => {
    try {
      setLoading(true);
      await lostSalesEventsService.patch(event._id, { active });
      toast.success(active ? "Lost Sales event activated." : "Lost Sales event deactivated.");
      await loadEvents();
    } catch (requestError) {
      console.error("Unable to update Lost Sales event:", requestError);
      toast.error("Unable to update Lost Sales event.");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (event) => {
    if (!window.confirm(`Delete "${event.name}"?`)) return;

    try {
      setLoading(true);
      await lostSalesEventsService.remove(event._id);
      toast.success("Lost Sales event deleted.");
      await loadEvents();
    } catch (requestError) {
      console.error("Unable to delete Lost Sales event:", requestError);
      toast.error("Unable to delete Lost Sales event.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsSection
      testId="lost-sales-events-settings"
      title="Lost Sales Events"
      description="Active date ranges are excluded from demand baseline windows."
      action={
        <SectionAction
          disabled={loading}
          onClick={openAdd}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        >
          Add event
        </SectionAction>
      }
    >
      {events.length ? (
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            border: "1px solid var(--border-color)",
            borderRadius: 1.5,
          }}
        >
          {events.map((event) => {
            const active = event.active !== false;
            return (
              <Box
                component="li"
                key={event._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderBottom: "1px solid var(--border-color)",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ ...settingsText.value, fontWeight: 500 }}
                    noWrap
                  >
                    {event.name}
                  </Typography>
                  <Typography sx={settingsText.muted}>
                    {event.start_date} – {event.end_date}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={active ? "Active" : "Inactive"}
                  variant="outlined"
                  sx={{
                    fontFamily: "poppins",
                    fontSize: 11.5,
                    height: 22,
                    borderColor: active ? "success.light" : "var(--border-color)",
                    color: active ? "success.main" : "var(--text-secondary)",
                  }}
                />
                <Button
                  size="small"
                  disabled={loading}
                  onClick={() => setActive(event, !active)}
                  sx={{ ...settingsButtonSx, color: "var(--text-secondary)", px: 1 }}
                >
                  {active ? "Deactivate" : "Activate"}
                </Button>
                <Tooltip title="Edit">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`edit ${event.name}`}
                      disabled={loading}
                      onClick={() => openEdit(event)}
                      sx={iconButtonSx}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`delete ${event.name}`}
                      disabled={loading}
                      onClick={() => remove(event)}
                      sx={iconButtonSx}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography sx={settingsText.muted}>
          No event ranges configured.
        </Typography>
      )}

      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingEvent ? "Edit Lost Sales Event" : "Add Lost Sales Event"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "20px !important" }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            fullWidth
          />
          <TextField
            label="Start Date"
            type="date"
            value={form.start_date}
            onChange={(event) => setForm({ ...form, start_date: event.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={form.end_date}
            onChange={(event) => setForm({ ...form, end_date: event.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={loading} variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};

export default LostSalesEventsSettings;
